import * as XLSX from "xlsx";
import type { AssignmentSegment } from "../../domain/round/types";
import type { RoundWorkspacePayload } from "../../lib/api";

function safeFilename(value: string) {
  return value.normalize("NFKD").replace(/[^a-zA-Z0-9_-]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 90) || "export";
}

function clock(minutes: number | null | undefined) {
  if (minutes == null || !Number.isFinite(minutes)) return "";
  const value = Math.max(0, Math.round(minutes));
  return `${String(Math.floor(value / 60)).padStart(2, "0")}:${String(value % 60).padStart(2, "0")}`;
}

function assignmentLabel(segment: AssignmentSegment) {
  if (segment === "full18") return "Full 18";
  if (segment === "extraFull18") return "Dodatkowy full 18";
  if (segment === "holes1to9") return "1-9";
  if (segment === "holes10to18") return "10-18";
  if (segment === "extra1to9") return "Dodatkowe 1-9";
  if (segment === "extra10to18") return "Dodatkowe 10-18";
  return segment;
}

export function exportRoundExcel(workspace: RoundWorkspacePayload, tournamentName: string) {
  if (!workspace.schedule) throw new Error("Brak rozpiski do eksportu Excel.");
  const wb = XLSX.utils.book_new();
  const people = new Map(workspace.staff.map((row) => [row.personId, row.displayName]));
  for (const row of workspace.schedule.people) people.set(row.personId, row.displayName);

  const byGroup = new Map<string, typeof workspace.schedule.assignments>();
  for (const assignment of workspace.schedule.assignments) {
    const rows = byGroup.get(assignment.groupId) || [];
    rows.push(assignment);
    byGroup.set(assignment.groupId, rows);
  }

  const scoring = [...workspace.schedule.groups]
    .sort((a, b) => a.startMinutes - b.startMinutes || a.number - b.number)
    .map((group) => {
      const assignments = byGroup.get(group.id) || [];
      const names = (segments: AssignmentSegment[]) => assignments
        .filter((row) => segments.includes(row.segment))
        .map((row) => people.get(row.personId) || row.personId)
        .join(" / ");
      return {
        Grupa: group.number,
        Tee: group.tee ?? "",
        Start: clock(group.startMinutes),
        "Finish 9": clock(group.finish9Minutes),
        "Finish 18": clock(group.finish18Minutes),
        TV: group.tv ? "TAK" : "NIE",
        "Wymagana obsada": group.requiredScorers ?? group.coverageScorers ?? (group.tv ? 2 : 1),
        "Coverage": group.coverageScorers ?? group.requiredScorers ?? (group.tv ? 2 : 1),
        "Tee/Pin": group.teePin ? "TAK" : "NIE",
        "Full 18": names(["full18"]),
        "Dodatkowy Full 18": names(["extraFull18"]),
        "1-9": names(["holes1to9", "extra1to9"]),
        "10-18": names(["holes10to18", "extra10to18"]),
        Zawodnicy: group.playerNames.join(" / "),
      };
    });
  const scoringSheet = XLSX.utils.json_to_sheet(scoring);
  scoringSheet["!cols"] = [8, 7, 9, 10, 10, 7, 16, 10, 10, 24, 24, 24, 24, 48].map((wch) => ({ wch }));
  XLSX.utils.book_append_sheet(wb, scoringSheet, "Scoring");

  const assignments = workspace.schedule.assignments.map((row) => ({
    Osoba: people.get(row.personId) || row.personId,
    Grupa: workspace.schedule?.groups.find((group) => group.id === row.groupId)?.number ?? row.groupId,
    Pozycja: assignmentLabel(row.segment),
    Źródło: row.source,
    Lock: row.locked ? "TAK" : "NIE",
  }));
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(assignments), "Przydziały");

  const personRows = workspace.schedule.people.map((row) => ({
    Osoba: row.displayName,
    Dołki: row.holes,
    Start: clock(row.dutyStartMinutes),
    Koniec: clock(row.dutyEndMinutes),
    "Najdłuższa przerwa": row.longestBreakMinutes,
    Kierowca: row.driverEligible ? "TAK" : "NIE",
    Transport: row.transportIncluded ? "TAK" : "NIE",
  }));
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(personRows), "Osoby");

  const vehicleNames = new Map(workspace.resources.vehicles.map((row) => [row.id, row.name]));
  const baseNames = new Map(workspace.resources.bases.map((row) => [row.id, row.name]));
  const transportRows = (direction: "Dojazd" | "Powrót", rows: NonNullable<RoundWorkspacePayload["transport"]>["departures"]) => rows.map((row) => ({
    Kierunek: direction,
    Auto: vehicleNames.get(row.vehicleId) || row.vehicleId,
    Baza: baseNames.get(row.baseId) || row.baseId,
    Kurs: row.tripNumber,
    Shuttle: row.shuttle ? "TAK" : "NIE",
    Wyjazd: clock(row.departAtMinutes),
    Przyjazd: clock(row.arriveAtMinutes),
    Kierowca: people.get(row.driverPersonId) || row.driverPersonId,
    Pasażerowie: row.passengerPersonIds.map((id) => people.get(id) || id).join(" / "),
    "Spread / wait": row.waitSpreadMinutes,
  }));
  const transport = workspace.transport
    ? [...transportRows("Dojazd", workspace.transport.departures), ...transportRows("Powrót", workspace.transport.returns)]
    : [];
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(transport), "Transport");

  const metrics = workspace.transport?.metrics;
  const summary = [
    ["Turniej", tournamentName], ["Runda", workspace.round], ["Źródło", workspace.selectedSource], ["Workspace revision", workspace.workspaceRevision],
    ["Confirmed", workspace.confirmation.confirmed ? "TAK" : "NIE"], ["Transport stale", workspace.transportStale ? "TAK" : "NIE"],
    ["Morning max wait", metrics?.morningMaxWaitMinutes ?? ""], ["Return max wait", metrics?.returnMaxWaitMinutes ?? ""],
    ["Morning total wait", metrics?.morningTotalWaitMinutes ?? ""], ["Return total wait", metrics?.returnTotalWaitMinutes ?? ""],
  ];
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(summary), "Podsumowanie");

  XLSX.writeFile(wb, `${safeFilename(tournamentName)}-${workspace.round}-operacyjny.xlsx`, { compression: true });
}
